/** Framework-independent styles that are also assignable to React's canvas style prop. */
export interface CanvasLayerStyle {
  position: 'fixed' | 'absolute';
  inset: number;
  width: string;
  height: string;
  pointerEvents: 'none';
  zIndex?: number;
}

const DEFAULT_Z_INDEX = 10000;

/**
 * Recommended inline style for a full-viewport, click-through particle canvas
 * so particles render on top of the page without blocking clicks or other interaction.
 * Use with useParticles (spread as canvasStyle) or with a vanilla canvas element.
 */
export const particlesBackgroundLayerStyle: CanvasLayerStyle = {
  position: 'fixed',
  inset: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: DEFAULT_Z_INDEX,
};

export function getParticlesBackgroundLayerStyle(zIndex?: number): CanvasLayerStyle {
  return zIndex !== undefined
    ? { ...particlesBackgroundLayerStyle, zIndex }
    : particlesBackgroundLayerStyle;
}

/**
 * Style for a container-aware particle canvas.
 * The canvas fills its parent container using absolute positioning.
 * The parent element must have `position: relative` (or absolute/fixed).
 */
export const particlesContainerLayerStyle: CanvasLayerStyle = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
};

export function getParticlesContainerLayerStyle(zIndex?: number): CanvasLayerStyle {
  return zIndex !== undefined
    ? { ...particlesContainerLayerStyle, zIndex }
    : particlesContainerLayerStyle;
}

export { DEFAULT_Z_INDEX as particlesDefaultZIndex };

/**
 * Imperatively apply positioning styles to a canvas element.
 * Used by createParticles() / startScreensaver() for auto-created canvases
 * so users don't need to set any styles manually.
 */
export function applyCanvasStyles(
  canvas: HTMLCanvasElement,
  container?: HTMLElement,
  zIndex?: number
): void {
  const style = container
    ? getParticlesContainerLayerStyle(zIndex)
    : getParticlesBackgroundLayerStyle(zIndex);
  Object.assign(canvas.style, style);
}
